import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Tooltip } from 'antd';
import classnames from 'classnames';
import AvatarTag from '@web-common/components/UI/Avatar/avatarTag';
import { ContactActions, MailActions, MailTemplateActions, TempContactActions, useActions, useAppSelector } from '@web-common/state/createStore';
import { WriteContext } from '@web-setting/Mail/components/CustomTemplate/template_add_modal';
import IconCard from '@web-common/components/UI/IconCard';
import { getEdmSendConflictConfig, verifyEmail, isInvalidRoleContactModel } from '../../util';
import styles from './selector.module.scss';
import { useContactModel } from '@web-common/hooks/useContactModel';
import { emailRoleToText, transMailContactModel2ContactItem } from '@web-common/utils/contact_util';
import { doGetMailContactModelByContactItem } from '@web-common/state/selector/contact';
import { transContactModel2ContactItem } from '@web-common/components/util/contact';
import { useSelectorContext } from './SelectorContext';
const ContactChip = props => {
    var _a;
    const { value, item, itemIndex, isLast, type, onDoubleClick, setInputOrder, setContactCardVisible, clickShowContactCard, setLastItemPos, sendMailConflictConfig, } = props;
    const { isMailTemplate } = useContext(WriteContext);
    const { contactItemList, selectedEmails: selectedMails, selectedEmailsSet } = useSelectorContext();
    const contactItem = useMemo(() => ('email' in item ? item : transMailContactModel2ContactItem(item)), [item]);
    const email = contactItem.email || value;
    const useCompositeQuery = process.env.BUILD_ISEDM ? false : (contactItem === null || contactItem === void 0 ? void 0 : contactItem.type) === 'personal';
    const contactModel = useContactModel({ email, contactId: contactItem.id, useCompositeQuery });
    const isInvalidRole = process.env.BUILD_ISEDM &&
        useMemo(() => contactModel && isInvalidRoleContactModel(contactModel, sendMailConflictConfig || getEdmSendConflictConfig()), [contactModel, sendMailConflictConfig]);
    const isSelected = useAppSelector(state => {
        const emails = isMailTemplate ? state.tempContactReducer.selectedTags.emails : state.contactReducer.selectedTags.emails;
        return selectedEmailsSet.has(email);
    });
    const selectType = useAppSelector(state => {
        return isMailTemplate ? state.tempContactReducer.selectedTags.type : state.contactReducer.selectedTags.type;
    });
    const onContactActions = isMailTemplate ? TempContactActions : ContactActions;
    const { doSelectTags } = useActions(onContactActions);
    const onActions = isMailTemplate ? MailTemplateActions : MailActions;
    const { doModifyReceiver } = useActions(onActions);
    const isChosen = isSelected && type === selectType;
    const truncateValue = (email === null || email === void 0 ? void 0 : email.length) > 20 ? `${email.slice(0, 18)}...` : email;
    const correctEmail = verifyEmail(email === null || email === void 0 ? void 0 : email.trim());
    const tagRef = useRef(null);
    const searchElRef = useRef(null);
    const dragStart = useCallback((e) => {
        var _a, _b, _c, _d, _e, _f;
        const draggedData = contactItemList.filter(contactItem => selectedEmailsSet.has(contactItem.email));
        let targetIsSelected = false;
        setContactCardVisible(false);
        const target = e.currentTarget;
        const targetMail = target === null || target === void 0 ? void 0 : target.getAttribute('data-email');
        const targetType = target === null || target === void 0 ? void 0 : target.getAttribute('data-type');
        targetIsSelected = draggedData.some(item => item.email === targetMail && targetType === type);
        const parent = document.querySelector('#drag-active-tags');
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
        }
        if (targetIsSelected) {
            const nodes = document.querySelectorAll('.ant-select-selector div[data-selected="true"]');
            nodes.forEach((node, index) => {
                if (index > 3)
                    return;
                if (index === 3) {
                    parent && parent.append('...');
                    return;
                }
                const clonedNode = node.cloneNode(true);
                clonedNode.style.marginRight = '10px';
                parent && parent.appendChild(clonedNode);
            });
            (_a = e.dataTransfer) === null || _a === void 0 ? void 0 : _a.setData('label', JSON.stringify(draggedData));
        }
        else {
            const nodes = document.querySelectorAll('.ant-select-selector');
            (_b = Array.from(nodes)) === null || _b === void 0 ? void 0 : _b.forEach(tag => {
                tag.classList.add('single-draging-outer');
            });
            target.classList.add('single-draging');
            const clonedNode = target === null || target === void 0 ? void 0 : target.cloneNode(true);
            parent && parent.appendChild(clonedNode);
            (_c = e.dataTransfer) === null || _c === void 0 ? void 0 : _c.setData('label', JSON.stringify([item]));
        }
        if (parent) {
            (_d = e.dataTransfer) === null || _d === void 0 ? void 0 : _d.setDragImage(parent, 10, 10);
        }
        (_e = e.dataTransfer) === null || _e === void 0 ? void 0 : _e.setData('text/plain', 'need');
        (_f = e.dataTransfer) === null || _f === void 0 ? void 0 : _f.setData('type', type);
    }, [contactItemList, selectedEmailsSet, type, setContactCardVisible]);
    useEffect(() => {
        var _a;
        if (itemIndex >= 0 && isLast) {
            const elRect = (_a = tagRef === null || tagRef === void 0 ? void 0 : tagRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            elRect && setLastItemPos([elRect.top, elRect.right]);
        }
    }, [isLast]);
    const dragEnd = useCallback(() => {
        var _a, _b;
        const parent = document.querySelector('#drag-active-tags');
        const nodes = document.querySelectorAll('.contact-tag-drop-item');
        (_a = Array.from(nodes)) === null || _a === void 0 ? void 0 : _a.forEach(tag => {
            var _a, _b, _c;
            if ((_a = tag === null || tag === void 0 ? void 0 : tag.classList) === null || _a === void 0 ? void 0 : _a.contains(styles.capDropEffect)) {
                (_b = tag === null || tag === void 0 ? void 0 : tag.classList) === null || _b === void 0 ? void 0 : _b.remove(styles.capDropEffect);
            }
            if (tag === null || tag === void 0 ? void 0 : tag.classList.contains('single-draging')) {
                (_c = tag === null || tag === void 0 ? void 0 : tag.classList) === null || _c === void 0 ? void 0 : _c.remove('single-draging');
            }
        });
        const outerDom = document.querySelectorAll('.ant-select-selector.single-draging-outer');
        (_b = Array.from(outerDom)) === null || _b === void 0 ? void 0 : _b.forEach(tag => {
            var _a;
            (_a = tag === null || tag === void 0 ? void 0 : tag.classList) === null || _a === void 0 ? void 0 : _a.remove('single-draging-outer');
        });
        if (parent)
            parent.innerHTML = '';
    }, []);
    const handleSort = async (e) => {
        var _a;
        try {
            const draggingData = (_a = e === null || e === void 0 ? void 0 : e.dataTransfer) === null || _a === void 0 ? void 0 : _a.getData('label');
            const _draggingData = JSON.parse(draggingData);
            const targetEle = e.target;
            const first_draggingData = _draggingData[0];
            const targetIndex = contactItemList === null || contactItemList === void 0 ? void 0 : contactItemList.findIndex(c => c.email === email);
            const first_draggingData_index = contactItemList === null || contactItemList === void 0 ? void 0 : contactItemList.findIndex(c => c.email === first_draggingData.email);
            let deleted = [];
            if (selectedEmailsSet.has(first_draggingData === null || first_draggingData === void 0 ? void 0 : first_draggingData.email)) {
                deleted = contactItemList === null || contactItemList === void 0 ? void 0 : contactItemList.filter(c => !selectedEmailsSet.has(c.email));
            }
            else {
                deleted = contactItemList === null || contactItemList === void 0 ? void 0 : contactItemList.filter(c => c.email !== first_draggingData.email);
            }
            let indexGap = 1;
            if (targetIndex <= first_draggingData_index) {
                indexGap = _draggingData.length;
                deleted === null || deleted === void 0 ? void 0 : deleted.splice(targetIndex, 0, ..._draggingData);
            }
            else {
                deleted === null || deleted === void 0 ? void 0 : deleted.splice(targetIndex - _draggingData.length, 0, ..._draggingData);
                indexGap = 0;
            }
            const receiver = await doGetMailContactModelByContactItem(deleted, type);
            doModifyReceiver({ receiverType: type, receiver });
            setInputOrder(deleted.length - targetIndex - indexGap);
            setTimeout(() => {
                let parent = targetEle;
                while (parent && !parent.classList.contains('ant-select-selector') && parent.tagName !== 'BODY') {
                    parent = parent.parentElement;
                }
                if (!parent)
                    return;
                let searchEl = parent.querySelectorAll('.ant-select-selection-search');
                if (searchEl && searchEl[0]) {
                    searchElRef.current = searchEl[0];
                    searchEl[0].classList.add('focused');
                }
                const selectedEmails = (_draggingData === null || _draggingData === void 0 ? void 0 : _draggingData.map((d) => d.email)) || [];
                doSelectTags({ emails: selectedEmails, type });
            }, 0);
        }
        catch (error) {
            console.log('dropListenerError', error);
        }
    };
    const handleSelectTags = (e) => {
        const { metaKey, button, detail, shiftKey, ctrlKey, altKey } = e;
        if (button !== 0)
            return;
        if (detail === 2) {
            e.preventDefault();
            e.stopPropagation();
            onDoubleClick && onDoubleClick(e);
            return;
        }
        const email = (contactModel === null || contactModel === void 0 ? void 0 : contactModel.contact.accountName) || contactItem.email;
        const isSelectingSameLine = selectType === type;
        if (selectedEmailsSet.has(email)) {
            doSelectTags({
                emails: isSelectingSameLine ? selectedMails.filter(selected => selected !== email) : [email],
                type,
            });
            return;
        }
        if (metaKey || ctrlKey || altKey) {
            doSelectTags({
                emails: isSelectingSameLine ? [...selectedMails, email] : [email],
                type,
            });
        }
        else if (shiftKey) {
            if (selectedMails.length === 0) {
                doSelectTags({
                    emails: [email],
                    type,
                });
                return;
            }
            let selectedEmails = [];
            const currrentEmails = contactItemList.map(info => info.email);
            const selectIndex = currrentEmails.findIndex(mail => mail === email);
            const selectedEmailsIndex = currrentEmails.map((mail, index) => (selectedEmailsSet.has(mail) ? index : undefined)).filter(mail => mail !== undefined);
            const maxIndex = Math.max.apply(null, selectedEmailsIndex);
            const minIndex = Math.min.apply(null, selectedEmailsIndex);
            if (selectIndex < minIndex) {
                selectedEmails = currrentEmails.filter((_mail, index) => index >= selectIndex && index <= maxIndex);
            }
            if (selectIndex > maxIndex) {
                selectedEmails = currrentEmails.filter((_mail, index) => index >= minIndex && index <= selectIndex);
            }
            doSelectTags({
                emails: selectedEmails,
                type,
            });
        }
        else {
            doSelectTags({
                emails: [email],
                type,
            });
        }
    };
    useEffect(() => {
        if (tagRef.current) {
            tagRef.current.addEventListener('dragstart', dragStart);
            tagRef.current.addEventListener('dragend', dragEnd);
        }
        return () => {
            if (tagRef.current) {
                tagRef.current.removeEventListener('dragstart', dragStart);
                tagRef.current.removeEventListener('dragend', dragEnd);
            }
        };
    }, [dragStart, dragEnd]);
    const showContactCard = (e) => {
        var _a;
        const clientRects = (_a = tagRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        const { clientWidth } = document.body;
        const { clientHeight } = document.body;
        const pos = {
            top: clientRects.top,
            left: clientRects.left,
            bottom: clientHeight - clientRects.bottom,
            right: clientWidth - clientRects.right,
        };
        clickShowContactCard(pos, contactItem);
        e.stopPropagation();
    };
    let children;
    if (!correctEmail) {
        children = (React.createElement("div", { className: classnames(styles.capsuleTag, {
                [styles.capsuleTagWrong]: true,
                'tag-chosen': isChosen,
                'contact-tag': true,
            }) },
            React.createElement("span", { className: classnames([styles.charAvatar, styles.charAvatarWrong]) }, "!"),
            React.createElement("span", { className: classnames([styles.label, styles.labelWrong]) }, truncateValue)));
    }
    else if (!item) {
        children = (React.createElement("div", { className: classnames(styles.capsuleTag, {
                'tag-chosen': isChosen,
                'contact-tag': true,
            }) },
            React.createElement(AvatarTag, { style: { display: 'inline-block' }, size: 20, showAvatarFirst: false, user: {
                    name: value,
                    color: '#ffaa00',
                } }),
            React.createElement("span", { className: styles.label }, truncateValue)));
    }
    else {
        let realContactItem = contactItem;
        if (contactModel) {
            realContactItem = transContactModel2ContactItem(contactModel);
        }
        const { name, avatar, email: accountName, customerRole, id: realContactId } = realContactItem;
        let truncateName = name || accountName;
        const orgAvatar = React.createElement(AvatarTag, { size: 20, contactId: realContactId, propEmail: accountName, user: { name, avatar, email: accountName } });
        let edmAvatar = orgAvatar;
        const roleText = emailRoleToText(customerRole);
        truncateName = `${roleText ? roleText + ' ' : ''}` + truncateName;
        const isCustomer = ['myCustomer', 'colleagueCustomer', 'colleagueCustomerNoAuth', 'openSeaCustomer'].some(i => i === customerRole);
        const isClue = ['myClue', 'colleagueClue', 'colleagueClueNoAuth', 'openSeaClue'].some(i => i === customerRole);
        if (isClue) {
            edmAvatar = React.createElement(IconCard, { type: "hintClient" });
        }
        else if (isCustomer) {
            edmAvatar = React.createElement(IconCard, { type: "crmClient" });
        }
        if (isInvalidRole) {
            edmAvatar = React.createElement("span", { className: classnames([styles.charAvatar, styles.charAvatarWrong]) }, "!");
        }
        truncateName = (truncateName === null || truncateName === void 0 ? void 0 : truncateName.length) > 20 ? `${truncateName.slice(0, 18)}...` : truncateName;
        const selected = correctEmail && isSelected;
        let arrowRightColor = '#6F7485';
        if (selected)
            arrowRightColor = '#FFFFFF';
        children = (React.createElement("div", { data: email, className: classnames(styles.capsuleTag, {
                'tag-chosen': isChosen,
                'contact-tag': true,
                [styles.clientBackgroundColor]: !isInvalidRole && isCustomer,
                [styles.hintBackgroundColor]: !isInvalidRole && isClue,
                [styles.capsuleTagWrong]: isInvalidRole,
            }) },
            process.env.BUILD_ISEDM ? edmAvatar : orgAvatar,
            React.createElement("span", { className: classnames({
                    [styles.label]: true,
                    [styles.labelWrong]: isInvalidRole,
                }) }, truncateName),
            !isInvalidRole && (React.createElement("span", { className: classnames(styles.crmCardControl), onMouseDown: showContactCard, onMouseUp: e => e.stopPropagation(), "data-test-id": "write-contact-chip-arrow" },
                React.createElement(IconCard, { className: "dark-invert", type: "arrowRight", stroke: arrowRightColor })))));
    }
    const chip = (React.createElement("div", { "data-selected": !!(correctEmail && isSelected), draggable: "true", className: "contact-tag-drop-item", "data-test-id": "write-contact-chip", "data-email": email, "data-type": type, ref: tagRef, style: {
            background: 'transparent',
            transform: 'translate(0,0)',
            zIndex: 9999,
            border: '2px solid trasparent',
            paddingLeft: 4,
        }, onMouseUp: handleSelectTags, onMouseDown: e => e.stopPropagation(), onDrop: handleSort }, children));
    const handleDragOver = useCallback(() => {
        if (!tagRef.current.classList.contains(styles.capDropEffect)) {
            tagRef.current.classList.add(styles.capDropEffect);
        }
    }, []);
    const handleDragLeave = useCallback(() => {
        if (tagRef.current.classList.contains(styles.capDropEffect)) {
            tagRef.current.classList.remove(styles.capDropEffect);
        }
    }, []);
    useEffect(() => {
        if (tagRef.current) {
            tagRef.current.addEventListener('dragover', handleDragOver);
            tagRef.current.addEventListener('dragleave', handleDragLeave);
        }
        return () => {
            if (tagRef.current) {
                tagRef.current.removeEventListener('dragover', handleDragOver);
                tagRef.current.removeEventListener('dragleave', handleDragLeave);
            }
        };
    }, [handleDragOver, handleDragLeave]);
    const toolTipTitle = useMemo(() => {
        var _a;
        if (!correctEmail) {
            return `${email}（邮箱格式错误,请双击修改）`;
        }
        if (isInvalidRole) {
            const isCustomer = ((_a = contactModel === null || contactModel === void 0 ? void 0 : contactModel.customerOrgModel) === null || _a === void 0 ? void 0 : _a.role) === 'colleagueCustomer';
            return `${email}（管理员设置${isCustomer ? '同事' : '线索'}客户防冲突禁止发信）`;
        }
        return email;
    }, [correctEmail, email, isInvalidRole, (_a = contactModel === null || contactModel === void 0 ? void 0 : contactModel.customerOrgModel) === null || _a === void 0 ? void 0 : _a.role]);
    return (React.createElement(React.Fragment, null, isSelected ? (chip) : (React.createElement(Tooltip, { title: toolTipTitle, destroyTooltipOnHide: true, placement: "bottomLeft", overlayClassName: "selector-tag-tooltip" }, chip))));
};
export default ContactChip;
